import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from './config';
import AdminSidebar from './AdminSidebar';
import '../css/AdminLineLink.css';

function AdminLineLink() {
  const navigate = useNavigate();
  const actor = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user') || 'null'); }
    catch { return null; }
  }, []);
  const [lineAccounts, setLineAccounts] = useState([]);
  const [users, setUsers] = useState([]);
  const [sourceUserId, setSourceUserId] = useState('');
  const [targetUserId, setTargetUserId] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const embedMode = new URLSearchParams(window.location.search).get('embed') === '1';

  const authFetch = useCallback(async (path, options = {}) => {
    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        ...(options.headers || {})
      }
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.message || 'ทำรายการไม่สำเร็จ');
    return data;
  }, []);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await authFetch('/api/admin/line-links');
      setLineAccounts(data.lineAccounts || []);
      setUsers(data.users || []);
    } catch (loadError) {
      setError(loadError.message);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    if (!actor || String(actor.role || '').toLowerCase() !== 'admin') {
      navigate('/', { replace: true });
      return;
    }
    load();
  }, [actor, load, navigate]);

  const filteredUsers = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    if (!keyword) return users;
    return users.filter(user => String(user.name || '').toLowerCase().includes(keyword)
      || String(user.user_id).includes(keyword));
  }, [search, users]);

  const source = lineAccounts.find(user => Number(user.user_id) === Number(sourceUserId));
  const target = users.find(user => Number(user.user_id) === Number(targetUserId));

  const submit = async event => {
    event.preventDefault();
    if (!source || !target) return;
    if (!window.confirm(`ยืนยันผูก LINE ของ “${source.name}” กับ ${target.name} (ID ${target.user_id}) ใช่หรือไม่?`)) return;
    try {
      setBusy(true);
      setError('');
      setMessage('');
      const data = await authFetch('/api/admin/line-links', {
        method: 'POST',
        body: JSON.stringify({ sourceUserId: source.user_id, targetUserId: target.user_id })
      });
      setMessage(data.message);
      setSourceUserId('');
      setTargetUserId('');
      await load();
    } catch (submitError) {
      setError(submitError.message);
    } finally {
      setBusy(false);
    }
  };

  return <main className="admin-line-link-shell">
    {!embedMode && <AdminSidebar active="line-links" />}
    <section className="admin-line-link-page">
      <header><span>LINE ACCOUNT LINK</span><h1>🔗 ผูก LINE กับ Player ID</h1><p>เลือกชื่อที่เคยสมัครหรือ Login ด้วย LINE แล้วเลือกบัญชีผู้เล่นในระบบ</p></header>
      {error && <p className="admin-line-link-message error">⚠️ {error}</p>}
      {message && <p className="admin-line-link-message success">✅ {message}</p>}
      {loading ? <div className="admin-line-link-loading">กำลังโหลดข้อมูล…</div> : <form onSubmit={submit}>
        <label><b>1. เลือกชื่อบัญชี LINE</b><small>แสดงเฉพาะบัญชีที่เคย Login ด้วย LINE สำเร็จ</small>
          <select value={sourceUserId} onChange={event => setSourceUserId(event.target.value)} required>
            <option value="">— เลือกชื่อจาก LINE Login —</option>
            {lineAccounts.map(user => <option key={user.user_id} value={user.user_id}>{user.name} • ID {user.user_id}</option>)}
          </select>
        </label>
        <div className="admin-line-link-arrow">↓</div>
        <label><b>2. เลือก Player ID ที่ต้องการผูก</b><small>ค้นหาได้จากชื่อหรือหมายเลข ID</small>
          <input value={search} onChange={event => setSearch(event.target.value)} placeholder="ค้นหาชื่อหรือ Player ID" />
          <select value={targetUserId} onChange={event => setTargetUserId(event.target.value)} required size={Math.min(7, Math.max(3, filteredUsers.length))}>
            {filteredUsers.map(user => <option key={user.user_id} value={user.user_id}>{user.name} • ID {user.user_id}{user.line_linked ? ' • ผูก LINE แล้ว' : ''}</option>)}
          </select>
        </label>
        {source && target && <div className="admin-line-link-preview"><span>{source.name}<small>บัญชี LINE</small></span><strong>→</strong><span>{target.name}<small>Player ID {target.user_id}</small></span></div>}
        <button type="submit" disabled={busy || !source || !target}>{busy ? 'กำลังผูกบัญชี…' : '🔗 ยืนยันการผูก LINE'}</button>
        <p className="admin-line-link-note">การทำรายการจะย้ายเฉพาะการ Login ด้วย LINE ไปยัง Player ID ที่เลือก ประวัติ Match และข้อมูลผู้เล่นเดิมจะไม่ถูกลบ</p>
      </form>}
    </section>
  </main>;
}

export default AdminLineLink;
